import type { MetaArgs } from "react-router";

import { getPublicApiBaseUrl } from "~/constants/common";
import {
  INDEX_ROBOTS_DIRECTIVE,
  NOINDEX_ROBOTS_DIRECTIVE,
} from "~/constants/robots";
import { ROUTES } from "~/constants/routing";
import type { EventLoaderData } from "~/loaders/event";
import type { ICategory } from "~/types/category";
import type { IAggregatedEvent, IMarket } from "~/types/event";
import type { IFAQItem } from "~/types/FAQ";
import { ensureHttpsUrl } from "~/utils/ensureHttpsUrl";
import { formatCents } from "~/utils/formatPrice";
import { formatPercentageValue } from "~/utils/formatValue";
import { generateMetaData } from "~/utils/generateMetaData";
import { getSiteOrigin } from "~/utils/getSiteOrigin";
import { getRouteErrorKind } from "~/utils/routeErrorKind";
import { resolveShareCardImage } from "~/utils/shareCard";

const SITE_NAME = "PredictionHero";

const getEventDescription = (event: IAggregatedEvent) =>
  event.description?.trim() || event.title;

const getVisibleMarketPrice = (market: IMarket) =>
  formatCents(market.price ?? 0);

const getVisibleMarketProbability = (market: IMarket) =>
  formatPercentageValue(Math.round((market.chance ?? 0) * 100));

const anchorTagRegExp = /<a\s+[^>]*href=(["'])(.*?)\1[^>]*>(.*?)<\/a>/gi;

const getFaqAnswerText = (answer: string) =>
  answer.replace(anchorTagRegExp, "$3").replace(/<[^>]+>/g, "");

const buildFaqJsonLd = (faqs: IFAQItem[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: {
      "@type": "Answer",
      text: getFaqAnswerText(answer),
    },
  })),
});

const buildBreadcrumbJsonLd = ({
  event,
  category,
  pageUrl,
}: {
  event: IAggregatedEvent;
  category: ICategory | null;
  pageUrl: string;
}) => {
  const siteUrl = new URL("/", pageUrl).toString().replace(/\/$/, "");
  const items: { name: string; item?: string }[] = [
    { name: "Home", item: `${siteUrl}/` },
  ];

  if (category) {
    items.push({
      name: category.name,
      item: `${siteUrl}${ROUTES.category.createURL(String(category.slug))}`,
    });
  }

  items.push({ name: event.title });

  return {
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumb`,
    itemListElement: items.map(({ name, item }, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name,
      ...(item ? { item } : {}),
    })),
  };
};

const buildEventJsonLd = ({
  event,
  markets,
  category,
  pageUrl,
}: {
  event: IAggregatedEvent;
  markets: IMarket[];
  category: ICategory | null;
  pageUrl: string;
}) => {
  const siteUrl = new URL("/", pageUrl).toString();
  const description = getEventDescription(event);

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: event.title,
        description,
        publisher: {
          "@type": "Organization",
          name: SITE_NAME,
          url: siteUrl,
        },
        breadcrumb: {
          "@id": `${pageUrl}#breadcrumb`,
        },
        mainEntity: {
          "@id": `${pageUrl}#event`,
        },
        about: markets.length
          ? {
              "@id": `${pageUrl}#markets`,
            }
          : undefined,
      },
      buildBreadcrumbJsonLd({ event, category, pageUrl }),
      {
        "@type": "Event",
        "@id": `${pageUrl}#event`,
        name: event.title,
        description,
        url: pageUrl,
        ...(event.startDate ? { startDate: event.startDate } : {}),
        ...(event.endDate ? { endDate: event.endDate } : {}),
      },
      ...(markets.length
        ? [
            {
              "@type": "ItemList",
              "@id": `${pageUrl}#markets`,
              name: `${event.title} market prices`,
              itemListElement: markets.map((market, index) => {
                return {
                  "@type": "ListItem",
                  position: index + 1,
                  item: {
                    "@type": "Product",
                    name: market.title || event.title,
                    description: market.title || event.title,
                    url: pageUrl,
                    brand: {
                      "@type": "Organization",
                      name: market.platform,
                    },
                    additionalProperty: [
                      {
                        "@type": "PropertyValue",
                        name: "Current price",
                        value: getVisibleMarketPrice(market),
                      },
                      {
                        "@type": "PropertyValue",
                        name: "Current probability",
                        value: getVisibleMarketProbability(market),
                      },
                      {
                        "@type": "PropertyValue",
                        name: "Source",
                        value: market.platform,
                      },
                    ],
                  },
                };
              }),
            },
          ]
        : []),
    ],
  };
};

const fallbackEventJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "PredictionHero | Track Prediction Markets",
  description:
    "Track prediction markets in one place. Compare odds, volume, and market signals across major leagues, games, and global events.",
};

export const getEventMeta = (
  metaArgs: MetaArgs<unknown, Record<string, unknown>>
) => {
  const loaderData = metaArgs.loaderData as EventLoaderData | undefined;
  const event = loaderData?.event;
  const faqs = event?.seo.faqs ?? [];
  // `loaderData.pageUrl` is the SSR request URL, which arrives as `http://`
  // whenever TLS terminated at the proxy. Every `@id`/`url` below is a published
  // identifier that search engines match against the canonical https URL, so the
  // scheme is pinned here rather than inherited from the transport.
  const pageUrl = loaderData ? ensureHttpsUrl(loaderData.pageUrl) : undefined;
  const jsonLd =
    event && pageUrl
      ? [
          buildEventJsonLd({
            event,
            markets: loaderData.markets,
            category: loaderData.category,
            pageUrl,
          }),
          ...(faqs.length ? [buildFaqJsonLd(faqs)] : []),
        ]
      : [fallbackEventJsonLd];

  const shareCardImage =
    event && pageUrl
      ? resolveShareCardImage(
          pageUrl,
          loaderData.markets,
          getPublicApiBaseUrl(getSiteOrigin(pageUrl))
        )
      : null;

  // With no event this meta runs for the route's own error boundary: a 429/503
  // (core-api throttled or down) must not read as a missing page.
  const errorPageName =
    getRouteErrorKind(metaArgs.error) === "serviceUnavailable"
      ? "Temporarily unavailable"
      : "Page not found";

  return generateMetaData({
    pageName: event ? "Event" : errorPageName,
    metaArgs,
    seoData: event?.seo,
    categorySlug: loaderData?.category?.slug,
    pageUrl,
    ogImage: shareCardImage,
    twitterCard: shareCardImage ? "summary_large_image" : undefined,
    robotsDirective: event ? INDEX_ROBOTS_DIRECTIVE : NOINDEX_ROBOTS_DIRECTIVE,
    jsonLd,
  });
};
